import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import LessonModal from '../components/LessonModal';
import DeleteIconButton from '../components/DeleteIconButton';
import RenderMonitor from '../components/RenderMonitor';
import { supabase } from '../utils/supabase';
import { getCache, setCache, invalidateCache } from '../utils/cache';

function CourseDetail({ id }) {
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [onboarding, setOnboarding] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedLesson, setExpandedLesson] = useState(null);
  const [expandedOnboarding, setExpandedOnboarding] = useState(false);
  const [showLessonModal, setShowLessonModal] = useState(false);
  const [showOnboardingModal, setShowOnboardingModal] = useState(false);
  const [editLesson, setEditLesson] = useState(null);
  const [modalSection, setModalSection] = useState('quiz');
  const [showCreateLessonModal, setShowCreateLessonModal] = useState(false);
  const [createLessonTitle, setCreateLessonTitle] = useState('');
  const [creatingLesson, setCreatingLesson] = useState(false);
  const [createLessonError, setCreateLessonError] = useState('');
  const [createLessonMode, setCreateLessonMode] = useState(''); // '' | 'scratch' | 'existing'
  const [existingCourses, setExistingCourses] = useState([]);
  const [existingCourseId, setExistingCourseId] = useState('');
  const [existingLessons, setExistingLessons] = useState([]);
  const [existingLoading, setExistingLoading] = useState(false);
  const [existingError, setExistingError] = useState('');
  const [prefillDraft, setPrefillDraft] = useState(null);
  const [showPrefillLessonModal, setShowPrefillLessonModal] = useState(false);
  const [lessonToDelete, setLessonToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [dragLessonIdx, setDragLessonIdx] = useState(null);
  const [dragOverLessonIdx, setDragOverLessonIdx] = useState(null);
  const [showDeleteCourseModal, setShowDeleteCourseModal] = useState(false);
  const [deletingCourse, setDeletingCourse] = useState(false);
  const [deleteCourseError, setDeleteCourseError] = useState('');
  // Migrate lesson state
  const [showMigrateModal, setShowMigrateModal] = useState(false);
  const [lessonToMigrate, setLessonToMigrate] = useState(null);
  const [migrateCourses, setMigrateCourses] = useState([]);
  const [migrateTarget, setMigrateTarget] = useState('');
  const [migratePositionPreview, setMigratePositionPreview] = useState(null);
  const [migrating, setMigrating] = useState(false);
  const [migrateError, setMigrateError] = useState('');
  // Rename course state
  const [editingTitle, setEditingTitle] = useState(false);
  const [newCourseTitle, setNewCourseTitle] = useState('');
  const [savingTitle, setSavingTitle] = useState(false);
  const [titleError, setTitleError] = useState('');
  const SNAPSHOT_KEY = `course.${id}.snapshot.v1`;

  const loadFromCache = () => {
    const cached = getCache(SNAPSHOT_KEY, 2 * 60 * 1000);
    if (cached) {
      if (cached.course) setCourse(cached.course);
      if (cached.onboarding !== undefined) setOnboarding(cached.onboarding);
      if (Array.isArray(cached.lessons)) setLessons(cached.lessons);
      if (Array.isArray(cached.categories)) setCategories(cached.categories);
      return true;
    }
    return false;
  };

  const handleRefresh = async () => {
    invalidateCache(SNAPSHOT_KEY);
    await fetchData(true);
  };

  const fetchData = async (force = false) => {
    if (!force && loadFromCache()) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');
    
    console.log('[COURSE][DB] SELECT course', { courseId: id });
    const { data: courseData, error: courseError } = await supabase
      .from('courses')
      .select('id, name, reflection_llm_context')
      .eq('id', parseInt(id))
      .single();
    if (courseError) { setError(courseError.message); setLoading(false); return; }
    setCourse(courseData);

    console.log('[COURSE][DB] SELECT lessons (all) for course', { courseId: id });
    const { data: lessonsData, error: lessonsError } = await supabase
      .from('lessons')
      .select('lesson_id, position, title, lesson_type, description')
      .eq('course_id', parseInt(id))
      .order('position');
    if (lessonsError) { setError(lessonsError.message); setLoading(false); return; }

    const lessonIds = (lessonsData || []).map(l => l.lesson_id);
    let onboardingLesson = (lessonsData || []).find(l => l.lesson_type === 'onboarding') || null;
    const regularLessons = (lessonsData || []).filter(l => l.lesson_type !== 'onboarding');

    if (lessonIds.length === 0) {
      setOnboarding(null);
      setLessons([]);
      console.log('[COURSE][DB] SELECT course_categories', { courseId: id });
      const { data: categoriesData } = await supabase
        .from('course_categories')
        .select('id, category_name')
        .eq('course_id', parseInt(id));
      const cats = categoriesData || [];
      setCategories(cats);
      setCache(SNAPSHOT_KEY, { course: courseData, onboarding: null, lessons: [], categories: cats });
      setLoading(false);
      return;
    }

    console.log('[COURSE][DB] SELECT questions (batched)', { lessons: lessonIds.length });
    const { data: allQuestions, error: questionsError } = await supabase
      .from('questions')
      .select('id, question_type, "order", lesson_id')
      .in('lesson_id', lessonIds)
      .order('lesson_id', { ascending: true })
      .order('order', { ascending: true });
    if (questionsError) { setError(questionsError.message); setLoading(false); return; }

    const mcIds = (allQuestions || []).filter(q => q.question_type === 'multi_choice').map(q => q.id);
    const refIds = (allQuestions || []).filter(q => q.question_type === 'reflection').map(q => q.id);
    const infoIds = (allQuestions || []).filter(q => (
      q.question_type === 'information' || q.question_type === 'onboard_intro' || q.question_type === 'onboard_outro'
    )).map(q => q.id);

    let mcMap = {}; let mcOptMap = {};
    if (mcIds.length > 0) {
      console.log('[COURSE][DB] SELECT questions (mc) with embeds (batched)', { count: mcIds.length });
      const { data: mcDetails, error: mcErr } = await supabase
        .from('questions')
        .select('id, multi_choice_questions(question_text), multi_choice_options(option_text,is_correct)')
        .in('id', mcIds);
      if (!mcErr) {
        (mcDetails || []).forEach(r => {
          const qText = Array.isArray(r.multi_choice_questions)
            ? (r.multi_choice_questions[0] && r.multi_choice_questions[0].question_text) || ''
            : (r.multi_choice_questions && r.multi_choice_questions.question_text) || '';
          mcMap[r.id] = { question_text: qText };
          mcOptMap[r.id] = Array.isArray(r.multi_choice_options) ? r.multi_choice_options : [];
        });
      }
    }

    let refMap = {}; let refCatMap = {};
    if (refIds.length > 0) {
      console.log('[COURSE][DB] SELECT reflection_questions + categories (batched)', { count: refIds.length });
      const { data: refDetails, error: refErr } = await supabase
        .from('reflection_questions')
        .select('question_id, prompt_text, extra_details, follow_up_question, reflection_question_categories(course_categories(id, category_name))')
        .in('question_id', refIds);
      if (!refErr) {
        (refDetails || []).forEach(r => {
          refMap[r.question_id] = { prompt_text: r.prompt_text, extra_details: r.extra_details || '', follow_up_question: r.follow_up_question || '' };
          const cats = Array.isArray(r.reflection_question_categories) ? r.reflection_question_categories : [];
          refCatMap[r.question_id] = cats
            .map(c => c.course_categories && c.course_categories.id && c.course_categories.category_name
              ? { id: c.course_categories.id, category_name: c.course_categories.category_name }
              : null)
            .filter(Boolean);
        });
      }
    }

    let infoMap = {};
    if (infoIds.length > 0) {
      console.log('[COURSE][DB] SELECT information_questions (batched)', { count: infoIds.length });
      const { data: infoDetails, error: infoErr } = await supabase
        .from('information_questions')
        .select('question_id, title, body_text')
        .in('question_id', infoIds);
      if (!infoErr) {
        (infoDetails || []).forEach(r => { infoMap[r.question_id] = { title: r.title, body_text: r.body_text }; });
      }
    }

    const lessonIdToQuestions = new Map();
    (allQuestions || []).forEach(q => {
      const base = { id: q.id, type: q.question_type, order: q.order };
      let full = null;
      if (q.question_type === 'multi_choice') {
        const md = mcMap[q.id] || { question_text: '' };
        const opts = mcOptMap[q.id] || [];
        const answers = opts.map(o => o.option_text);
        const correct = opts.filter(o => o.is_correct).map(o => o.option_text);
        full = { ...base, question_text: md.question_text, options: opts.map(o => ({ text: o.option_text, is_correct: o.is_correct })), title: `Q${q.order}. (multi_choice)`, subtitle: md.question_text, answers, correct };
      } else if (q.question_type === 'reflection') {
        const rd = refMap[q.id] || { prompt_text: '', extra_details: '', follow_up_question: '' };
        const cats = refCatMap[q.id] || [];
        full = { ...base, question_text: rd.prompt_text, extra_details: rd.extra_details || '', follow_up_question: rd.follow_up_question || '', categories: cats, title: `Q${q.order}. (reflection)`, subtitle: rd.prompt_text };
      } else if (q.question_type === 'information') {
        const inf = infoMap[q.id] || { title: '', body_text: '' };
        full = { ...base, title: inf.title, body_text: inf.body_text, question_text: inf.title, subtitle: inf.body_text };
      } else if (q.question_type === 'onboard_intro' || q.question_type === 'onboard_outro') {
        const inf = infoMap[q.id] || { title: '', body_text: '' };
        full = { ...base, title: inf.title, body_text: inf.body_text, question_text: inf.title, subtitle: inf.body_text, order: null };
      }
      if (full) {
        if (!lessonIdToQuestions.has(q.lesson_id)) lessonIdToQuestions.set(q.lesson_id, []);
        lessonIdToQuestions.get(q.lesson_id).push(full);
      }
    });

    let orderedOnboardingQs = null;
    if (onboardingLesson) {
      const qsRaw = lessonIdToQuestions.get(onboardingLesson.lesson_id) || [];
      const intro = qsRaw.find(q => q.type === 'onboard_intro');
      const outro = qsRaw.find(q => q.type === 'onboard_outro');
      const normals = qsRaw.filter(q => q.type !== 'onboard_intro' && q.type !== 'onboard_outro').sort((a, b) => (a.order || 0) - (b.order || 0));
      const qs = [
        ...(intro ? [intro] : []),
        ...normals,
        ...(outro ? [outro] : []),
      ];
      orderedOnboardingQs = qs;
      setOnboarding({ ...onboardingLesson, questions: orderedOnboardingQs });
    } else {
      setOnboarding(null);
    }

    const lessonsWithQuestions = regularLessons.map(l => ({ ...l, questions: (lessonIdToQuestions.get(l.lesson_id) || []) }));
    setLessons(lessonsWithQuestions);

    console.log('[COURSE][DB] SELECT course_categories', { courseId: id });
    const { data: categoriesData, error: categoriesError } = await supabase
      .from('course_categories')
      .select('id, category_name')
      .eq('course_id', parseInt(id));
    const cats = categoriesError ? [] : (categoriesData || []);
    if (!categoriesError) setCategories(cats);

    setCache(SNAPSHOT_KEY, { course: courseData, onboarding: onboardingLesson ? { ...onboardingLesson, questions: orderedOnboardingQs || [] } : null, lessons: lessonsWithQuestions, categories: cats });
    setLoading(false);
  };

  useEffect(() => {
    fetchData(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const refreshCategories = useCallback(async () => {
    console.log('[COURSE][DB] SELECT course_categories (refresh)', { courseId: id });
    const { data, error } = await supabase
      .from('course_categories')
      .select('id, category_name')
      .eq('course_id', parseInt(id));
    if (!error) setCategories(data || []);
  }, [id]);

  const handleModalSave = useCallback((updatedLesson) => {
    if (!updatedLesson) return;
    const transformQuestions = (qs) => {
      return (qs || [])
        .slice()
        .sort((a, b) => (a.order || 0) - (b.order || 0))
        .map((q) => {
          if (q.type === 'multi_choice') {
            const answers = (q.options || []).map(o => o.text);
            const correct = (q.options || []).filter(o => o.is_correct).map(o => o.text);
            return {
              ...q,
              title: `Q${q.order}. (multi_choice)`,
              subtitle: q.question_text || '',
              answers,
              correct,
            };
          }
          if (q.type === 'information') {
            return {
              ...q,
              title: q.title || '',
              subtitle: q.body_text || '',
            };
          }
          if (q.type === 'onboard_intro' || q.type === 'onboard_outro') {
            return {
              ...q,
              title: q.title || '',
              subtitle: q.body_text || '',
              order: null,
            };
          }
          if (q.type === 'reflection') {
            return {
              ...q,
              title: `Q${q.order}. (reflection)`,
              subtitle: q.question_text || '',
            };
          }
          return q;
        });
    };
    let transformed = { ...updatedLesson, questions: transformQuestions(updatedLesson.questions) };
    // Ensure onboarding ordering: intro first, normals by order, outro last
    if (transformed.lesson_type === 'onboarding' || (onboarding && transformed.lesson_id === onboarding.lesson_id)) {
      const intro = transformed.questions.find(q => q.type === 'onboard_intro');
      const outro = transformed.questions.find(q => q.type === 'onboard_outro');
      const normals = transformed.questions.filter(q => q.type !== 'onboard_intro' && q.type !== 'onboard_outro').sort((a, b) => (a.order || 0) - (b.order || 0));
      transformed = { ...transformed, questions: [ ...(intro ? [intro] : []), ...normals, ...(outro ? [outro] : []) ] };
    }
    if (transformed.lesson_type === 'onboarding' || (onboarding && transformed.lesson_id === onboarding.lesson_id)) {
      setOnboarding(prev => {
        const next = { ...(prev || {}), ...transformed };
        setCache(SNAPSHOT_KEY, { course, onboarding: next, lessons, categories });
        return next;
      });
    } else {
      setLessons(prev => {
        const found = prev.some(l => l.lesson_id === transformed.lesson_id);
        const nextLessons = found ? prev.map(l => (l.lesson_id === transformed.lesson_id ? { ...l, ...transformed } : l)) : [...prev, transformed];
        setCache(SNAPSHOT_KEY, { course, onboarding, lessons: nextLessons, categories });
        return nextLessons;
      });
    }
  }, [SNAPSHOT_KEY, course, onboarding, lessons, categories]);

  const handleDeleteLesson = (lesson) => {
    setLessonToDelete(lesson);
    setDeleteError('');
  };

  const confirmDeleteLesson = async () => {
    if (!lessonToDelete) return;
    
    setDeleting(true);
    setDeleteError('');
    
    try {
      const isOnboarding = lessonToDelete.lesson_type === 'onboarding';
      console.log('[COURSE][DB] SELECT questions for deletion', { lessonId: lessonToDelete.lesson_id });
      const { data: questionsData, error: questionsError } = await supabase
        .from('questions')
        .select('id')
        .eq('lesson_id', lessonToDelete.lesson_id);
      if (questionsError) { setDeleteError('Failed to fetch questions for deletion'); setDeleting(false); return; }
      for (const question of questionsData || []) {
        console.log('[COURSE][DB] DELETE question', { questionId: question.id });
        await supabase.from('questions').delete().eq('id', question.id);
      }
      console.log('[COURSE][DB] DELETE lesson', { lessonId: lessonToDelete.lesson_id });
      const { error: lessonError } = await supabase.from('lessons').delete().eq('lesson_id', lessonToDelete.lesson_id);
      if (lessonError) { setDeleteError('Failed to delete lesson'); setDeleting(false); return; }
      // Optimistically update state without full reload, and persist reindexed positions
      if (isOnboarding) {
        setOnboarding(null);
        setCache(SNAPSHOT_KEY, { course, onboarding: null, lessons, categories });
      } else {
        // Compute reindexed lessons based on current state and persist to DB
        const filtered = (lessons || []).filter(l => l.lesson_id !== lessonToDelete.lesson_id);
        const reindexed = filtered.map((l, idx) => ({ ...l, position: idx + 1 }));
        for (const l of reindexed) {
          try {
            console.log('[COURSE][DB] UPDATE lesson position (reindex after delete)', { lessonId: l.lesson_id, position: l.position });
            await supabase.from('lessons').update({ position: l.position }).eq('lesson_id', l.lesson_id);
          } catch (e) {}
        }
        setLessons(() => {
          setCache(SNAPSHOT_KEY, { course, onboarding, lessons: reindexed, categories });
          return reindexed;
        });
      }
      setLessonToDelete(null);
      setDeleting(false);
    } catch (err) {
      setDeleteError('An unexpected error occurred while deleting the lesson');
      setDeleting(false);
    }
  };

  const cancelDeleteLesson = () => {
    setLessonToDelete(null);
    setDeleteError('');
  };

  const confirmDeleteCourse = async () => {
    setDeletingCourse(true);
    setDeleteCourseError('');
    try {
      console.log('[COURSE][DB] SELECT bootcamps by course (usage check)', { courseId: id });
      const { data: usage, error: usageErr } = await supabase
        .from('bootcamps')
        .select('id')
        .eq('course_id', parseInt(id))
        .limit(1);
      if (usageErr) {
        setDeleteCourseError('Failed to check course usage');
        setDeletingCourse(false);
        return;
      }
      if (!usage || usage.length === 0) {
        console.log('[COURSE][DB] DELETE course', { courseId: id });
        const { error: delErr } = await supabase
          .from('courses')
          .delete()
          .eq('id', parseInt(id));
        if (delErr) {
          setDeleteCourseError('Failed to delete course');
          setDeletingCourse(false);
          return;
        }
      } else {
        console.log('[COURSE][DB] ARCHIVE course', { courseId: id });
        const { error: archErr } = await supabase
          .from('courses')
          .update({ active: false })
          .eq('id', parseInt(id));
        if (archErr) {
          setDeleteCourseError('Failed to archive course');
          setDeletingCourse(false);
          return;
        }
      }
      invalidateCache(SNAPSHOT_KEY);
      setDeletingCourse(false);
      setShowDeleteCourseModal(false);
      navigate('/admin/courses');
    } catch (err) {
      setDeleteCourseError('An unexpected error occurred while deleting/archiving the course');
      setDeletingCourse(false);
    }
  };

  const clearLessonDragState = () => {
    setDragLessonIdx(null);
    setDragOverLessonIdx(null);
  };

  const handleLessonDragStart = (idx) => {
    setDragLessonIdx(idx);
  };

  const handleLessonDragOver = (e, idx) => {
    e.preventDefault();
    setDragOverLessonIdx(idx);
  };

  const handleLessonDragLeave = (e, idx) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverLessonIdx((current) => (current === idx ? null : current));
    }
  };

  const handleLessonDrop = async (e, toIdx) => {
    e.preventDefault();
    const fromIdx = dragLessonIdx;
    clearLessonDragState();
    if (fromIdx === null || fromIdx === toIdx) return;

    const newLessons = [...lessons];
    const [moved] = newLessons.splice(fromIdx, 1);
    newLessons.splice(toIdx, 0, moved);
    const reassigned = newLessons.map((l, i) => ({ ...l, position: i + 1 }));
    setLessons(reassigned);

    for (let i = 0; i < reassigned.length; i++) {
      const l = reassigned[i];
      try {
        console.log('[COURSE][DB] UPDATE lesson position (drag)', { lessonId: l.lesson_id, position: l.position });
        await supabase.from('lessons').update({ position: l.position }).eq('lesson_id', l.lesson_id);
      } catch (err) {}
    }
  };

  const handleLessonDragEnd = () => {
    clearLessonDragState();
  };

  if (loading) {
    return (
      <div className="dashboard-page">
        <div className="modern-loading">Loading course…</div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="dashboard-page">
        <div className="modern-error">{error}</div>
      </div>
    );
  }

  return (
    <div className="dashboard-page">
      <RenderMonitor componentName="CourseDetail" />

      <div className="modern-flex modern-justify-between modern-items-center modern-mb-6">
        <button
          type="button"
          onClick={() => navigate('/admin/courses')}
          className="modern-button modern-button-secondary"
        >
          ← All Courses
        </button>
        <button
          type="button"
          className="modern-button modern-button-danger"
          onClick={() => { setDeleteCourseError(''); setShowDeleteCourseModal(true); }}
        >
          Delete Course
        </button>
      </div>

      <div className="modern-card modern-mb-6">
        <div className="modern-flex modern-justify-between modern-items-center modern-flex-wrap modern-gap-3">
          <div>
            {!editingTitle ? (
              <h1 className="modern-title" style={{ marginBottom: 0 }}>{course?.name || 'Course'}</h1>
            ) : (
              <div className="modern-flex modern-items-center modern-gap-2 modern-flex-wrap">
                <input
                  type="text"
                  value={newCourseTitle}
                  onChange={e => { setNewCourseTitle(e.target.value); setTitleError(''); }}
                  className="modern-input"
                  placeholder="Course Title"
                  disabled={savingTitle}
                  style={{ minWidth: 260 }}
                />
                <button
                  type="button"
                  className="modern-button modern-button-success"
                  disabled={savingTitle}
                  onClick={async () => {
                  const name = (newCourseTitle || '').trim();
                  if (!name) { setTitleError('Course title is required'); return; }
                  try {
                    setSavingTitle(true);
                    const { error } = await supabase
                      .from('courses')
                      .update({ name })
                      .eq('id', parseInt(id))
                      .select('id, name')
                      .single();
                    setSavingTitle(false);
                    if (error) { setTitleError('Failed to rename course'); return; }
                    setCourse(prev => ({ ...(prev || {}), id: parseInt(id), name }));
                    setEditingTitle(false);
                    // Update cache snapshot
                    setCache(SNAPSHOT_KEY, { course: { ...(course || {}), id: parseInt(id), name }, onboarding, lessons, categories });
                    // Update admin courses cache name
                    try {
                      const CLIENTS_CACHE_KEY = 'admin.courses.v1';
                      const raw = localStorage.getItem(CLIENTS_CACHE_KEY);
                      if (raw) {
                        const parsed = JSON.parse(raw);
                        const list = Array.isArray(parsed?.data) ? parsed.data : [];
                        const updated = list.map(c => String(c.id) === String(id) ? { ...c, name } : c);
                        localStorage.setItem(CLIENTS_CACHE_KEY, JSON.stringify({ savedAt: Date.now(), data: updated }));
                      }
                    } catch {}
                  } catch (e) {
                    setSavingTitle(false);
                    setTitleError('Unexpected error renaming course');
                  }
                }}
              >
                {savingTitle ? 'Saving…' : 'Save'}
                </button>
                <button
                  type="button"
                  className="modern-button modern-button-secondary"
                  disabled={savingTitle}
                  onClick={() => { setEditingTitle(false); setTitleError(''); }}
                >
                  Cancel
                </button>
              </div>
            )}
            {titleError && <div className="modern-error" style={{ marginTop: 8 }}>{titleError}</div>}
          </div>
          <div className="modern-flex modern-items-center modern-gap-2 modern-flex-wrap">
            {!editingTitle && (
              <button
                type="button"
                className="modern-button modern-button-secondary"
                onClick={() => { setEditingTitle(true); setNewCourseTitle(course?.name || ''); setTitleError(''); }}
              >
                Change Course Name
              </button>
            )}
            <button type="button" onClick={handleRefresh} className="modern-button modern-button-secondary">
              Refresh
            </button>
          </div>
        </div>
      </div>

      <div className="modern-flex modern-justify-between modern-items-center modern-mb-4">
        <h2 className="modern-heading" style={{ marginBottom: 0 }}>Onboarding</h2>
      </div>
      {onboarding ? (
        <div className="modern-list-panel course-detail-onboarding-panel modern-mb-6">
          <div className="course-detail-lesson-card">
            <div className="course-detail-lesson-header">
              <div
                className="course-detail-lesson-header-main"
                style={{ color: '#4f46e5' }}
                onClick={() => setExpandedOnboarding(!expandedOnboarding)}
              >
                🚀 Onboarding
                {onboarding.title && (
                  <span style={{ fontWeight: 'normal', fontSize: 16, fontStyle: 'italic' }}>: {onboarding.title}</span>
                )}
                {!onboarding.title && (
                  <span style={{ fontWeight: 'normal', fontSize: 16, fontStyle: 'italic', opacity: 0.6 }}>(Un-named)</span>
                )}
                {expandedOnboarding ? ' ▼' : ' ▶'}
              </div>
              <div className="course-detail-lesson-header-actions">
                <button
                  type="button"
                  className="modern-button modern-button-warning"
                  style={{ padding: '4px 10px', fontSize: 14 }}
                  onClick={e => { e.stopPropagation(); setEditLesson(onboarding); setModalSection(null); setShowOnboardingModal(true); }}
                >
                  Edit
                </button>
              </div>
              <div className="course-detail-lesson-header-delete">
                <DeleteIconButton
                  title="Delete onboarding"
                  onClick={(e) => { e.stopPropagation(); handleDeleteLesson(onboarding); }}
                />
              </div>
            </div>
            {expandedOnboarding && (
              <div className="course-detail-lesson-expanded">
              {onboarding.questions && onboarding.questions.length > 0 ? (
                onboarding.questions.map((q, idx) => (
                  <div key={q.id} style={{ marginBottom: 20, padding: 12, border: '1px solid #eee', borderRadius: 6, background: '#fafbfc' }}>
                    <div style={{ fontWeight: 'bold' }}>
                      {q.type === 'onboard_intro' ? 'Onboarding Intro Slide' : (q.type === 'onboard_outro' ? 'Onboarding Outro Slide' : (`Q${q.order}. (${q.type})`))}
                    </div>
                    {q.type === 'multi_choice' ? null : (q.type !== 'reflection' && q.type !== 'onboard_intro' && q.type !== 'onboard_outro') && (
                      <div style={{ fontWeight: 'bold', marginTop: 6 }}>{q.title}</div>
                    )}
                    {(q.type === 'onboard_intro' || q.type === 'onboard_outro') && q.title && (
                      <div style={{ fontWeight: '600', marginTop: 6 }}>{q.title}</div>
                    )}
                    {q.type === 'reflection' && (
                      <>
                        <div style={{ color: '#64748b', fontSize: 13, marginTop: 6 }}>Prompt:</div>
                        <div style={{ color: '#555', marginBottom: 8 }}>{q.subtitle}</div>
                      </>
                    )}
                    {q.type !== 'reflection' && q.subtitle && (
                      <div style={{ color: '#555', marginBottom: 8 }}>{q.subtitle}</div>
                    )}
                    {q.type === 'reflection' && (q.extra_details && q.extra_details.trim()) && (
                      <>
                        <div style={{ color: '#64748b', fontSize: 13, marginBottom: 4 }}>Extra details:</div>
                        <div style={{ color: '#666', marginBottom: 8, whiteSpace: 'pre-wrap' }}>{q.extra_details}</div>
                      </>
                    )}
                    {q.type === 'multi_choice' && (
                      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                        {q.answers.map((ans, i) => (
                          <div key={i} style={{ padding: '8px 14px', borderRadius: 6, border: '1px solid #bbb', background: q.correct.includes(ans) ? '#d4f7d4' : '#ffebee', minWidth: 80, textAlign: 'center' }}>{ans}</div>
                        ))}
                      </div>
                    )}
                    {q.type === 'reflection' && q.categories && q.categories.length > 0 && (
                      <div style={{ marginTop: 8 }}>
                        <div style={{ color: '#64748b', fontSize: 13, marginBottom: 6 }}>Choices:</div>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          {q.categories.map((cat, i) => (
                            <span key={cat.id || cat.category_name} style={{ background: '#e0e7ff', color: '#333', borderRadius: 16, padding: '4px 12px', fontSize: 14 }}>{cat.category_name}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    {q.type === 'reflection' && (q.follow_up_question && q.follow_up_question.trim()) && (
                      <>
                        <div style={{ color: '#64748b', fontSize: 13, marginTop: 8, marginBottom: 4 }}>Fallback Follow-Up Question:</div>
                        <div style={{ color: '#666', marginBottom: 8, whiteSpace: 'pre-wrap' }}>{q.follow_up_question}</div>
                      </>
                    )}
                  </div>
                ))
              ) : (
                <div style={{ color: '#888' }}>No questions for this onboarding.</div>
              )}
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="modern-mb-6">
          <button
            type="button"
            className="modern-button modern-button-success"
            onClick={() => { setEditLesson(null); setModalSection(null); setShowOnboardingModal(true); }}
          >
            + Add Onboarding
          </button>
        </div>
      )}
      
      <div className="modern-flex modern-justify-between modern-items-center modern-mb-4">
        <h2 className="modern-heading" style={{ marginBottom: 0 }}>Lessons</h2>
        <button
          type="button"
          className="modern-button modern-button-success"
          onClick={() => { setCreateLessonTitle(''); setCreateLessonError(''); setShowCreateLessonModal(true); }}
        >
          + Add Lesson
        </button>
      </div>
      <div className="modern-list-panel course-detail-lessons-panel modern-mb-6">
        {lessons.map((lesson, idx) => (
          <div
            key={lesson.lesson_id}
            draggable
            onDragStart={() => handleLessonDragStart(idx)}
            onDragOver={(e) => handleLessonDragOver(e, idx)}
            onDragLeave={(e) => handleLessonDragLeave(e, idx)}
            onDrop={(e) => handleLessonDrop(e, idx)}
            onDragEnd={handleLessonDragEnd}
            className={[
              'course-detail-lesson-card',
              dragLessonIdx === idx ? 'course-detail-lesson-card--dragging' : '',
              dragOverLessonIdx === idx && dragLessonIdx !== idx ? 'course-detail-lesson-card--drag-over' : '',
            ].filter(Boolean).join(' ')}
          >
            <div className="course-detail-lesson-header">
              <div
                className="course-detail-lesson-header-main"
                onClick={() => setExpandedLesson(lesson.lesson_id === expandedLesson ? null : lesson.lesson_id)}
              >
                <span style={{ color: '#94a3b8', fontSize: 18 }}>⋮⋮</span>
                Lesson {lesson.position}
                {lesson.title ? (
                  <span style={{ fontWeight: 'normal', fontSize: 16, color: '#64748b', fontStyle: 'italic' }}>: {lesson.title}</span>
                ) : (
                  <span style={{ fontWeight: 'normal', fontSize: 16, color: '#94a3b8', fontStyle: 'italic' }}>(Un-named)</span>
                )}
                {expandedLesson === lesson.lesson_id ? ' ▼' : ' ▶'}
              </div>
              <div className="course-detail-lesson-header-actions">
                <button
                  type="button"
                  className="modern-button modern-button-warning"
                  style={{ padding: '4px 10px', fontSize: 14 }}
                  onClick={e => { e.stopPropagation(); setEditLesson(lesson); setModalSection(null); setShowLessonModal(true); }}
                >
                  Change Lesson Name
                </button>
                <button
                  type="button"
                  className="modern-button modern-button-success"
                  style={{ padding: '4px 10px', fontSize: 14 }}
                  onClick={async e => {
                    e.stopPropagation();
                    setMigrateError('');
                    setLessonToMigrate(lesson);
                    setMigrateTarget('');
                    setMigratePositionPreview(null);
                    setShowMigrateModal(true);
                    try {
                      const { data: courseList } = await supabase
                        .from('courses')
                        .select('id, name, active')
                        .neq('id', parseInt(id))
                        .order('name');
                      setMigrateCourses(courseList || []);
                    } catch {}
                  }}
                >
                  Migrate
                </button>
              </div>
              <div className="course-detail-lesson-header-delete">
                <DeleteIconButton
                  title="Delete lesson"
                  onClick={(e) => { e.stopPropagation(); handleDeleteLesson(lesson); }}
                />
              </div>
            </div>
            {expandedLesson === lesson.lesson_id && (
              <div className="course-detail-lesson-expanded">
                <div style={{ marginBottom: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                    <h4 style={{ margin: 0 }}>Description</h4>
                    <button className="modern-button modern-button-warning"
                    style={{ padding: '4px 10px', fontSize: 14 }} onClick={e => { e.stopPropagation(); setEditLesson(lesson); setModalSection('description'); setShowLessonModal(true); }}>Edit</button>
                  </div>
                  {lesson.description && lesson.description.trim() ? (<div style={{ whiteSpace: 'pre-wrap', color: '#444' }}>{lesson.description}</div>) : (<div style={{ color: '#888' }}>No description.</div>)}
                </div>
                <div style={{ marginBottom: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                    <h4 style={{ margin: 0 }}>Quiz</h4>
                    <button className="modern-button modern-button-warning"
                    style={{ padding: '4px 10px', fontSize: 14 }} onClick={e => { e.stopPropagation(); setEditLesson(lesson); setModalSection('quiz'); setShowLessonModal(true); }}>Edit</button>
                  </div>
                  {lesson.questions && lesson.questions.filter(q => q.type === 'multi_choice' || q.type === 'information').length > 0 ? (
                    lesson.questions
                      .filter(q => q.type === 'multi_choice' || q.type === 'information')
                      .sort((a, b) => (a.order || 0) - (b.order || 0))
                      .map((q) => (
                        <div key={q.id} style={{ marginBottom: 12, padding: 12, border: '1px solid #eee', borderRadius: 6, background: '#fafbfc' }}>
                          <div style={{ fontWeight: 'bold' }}>{q.type === 'information' ? `Q${q.order}. (information)` : q.title}</div>
                          {q.type === 'information' && (
                            <div style={{ fontWeight: 600, marginTop: 6 }}>{q.title}</div>
                          )}
                          <div style={{ color: '#555', marginBottom: 8 }}>{q.subtitle}</div>
                          {q.type === 'multi_choice' && (
                            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                              {q.answers.map((ans, i) => (
                                <div key={i} style={{ padding: '8px 14px', borderRadius: 6, border: '1px solid #bbb', background: q.correct.includes(ans) ? '#d4f7d4' : '#ffebee', minWidth: 80, textAlign: 'center' }}>{ans}</div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))
                  ) : (
                    <div style={{ color: '#888' }}>No quiz questions.</div>
                  )}
                </div>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                    <h4 style={{ margin: 0 }}>Apply it on the Job</h4>
                    <button className="modern-button modern-button-warning"
                    style={{ padding: '4px 10px', fontSize: 14 }} onClick={e => { e.stopPropagation(); setEditLesson(lesson); setModalSection('apply'); setShowLessonModal(true); }}>Edit</button>
                  </div>
                  {lesson.questions && lesson.questions.filter(q => q.type === 'reflection').length > 0 ? (
                    lesson.questions.filter(q => q.type === 'reflection').sort((a, b) => (a.order || 0) - (b.order || 0)).map((q) => (
                      <div key={q.id} style={{ marginBottom: 12, padding: 12, border: '1px solid #eee', borderRadius: 6, background: '#fafbfc' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 4 }}>
                          <div style={{ fontWeight: 'bold' }}>{q.title}</div>
                          <button
                            type="button"
                            className="modern-button modern-button-secondary"
                            style={{ padding: '4px 10px', fontSize: 13, flexShrink: 0 }}
                            onClick={(e) => { e.stopPropagation(); navigate(`/course/${id}/reflection/${q.id}/test-ai`); }}
                          >
                            Test AI
                          </button>
                        </div>
                        <div style={{ color: '#64748b', fontSize: 13, marginTop: 6 }}>Prompt:</div>
                        <div style={{ color: '#555', marginBottom: 8 }}>{q.subtitle}</div>
                        {(q.extra_details && q.extra_details.trim()) && (
                          <>
                            <div style={{ color: '#64748b', fontSize: 13, marginBottom: 4 }}>Extra details:</div>
                            <div style={{ color: '#666', marginBottom: 8, whiteSpace: 'pre-wrap' }}>{q.extra_details}</div>
                          </>
                        )}
                        {q.categories && q.categories.length > 0 && (
                          <div style={{ marginTop: 8 }}>
                            <div style={{ color: '#64748b', fontSize: 13, marginBottom: 6 }}>Choices:</div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                              {q.categories.map((cat, i) => (
                                <span key={cat.id || cat.category_name} style={{ background: '#e0e7ff', color: '#333', borderRadius: 6, padding: '4px 12px', fontSize: 14 }}>{cat.category_name}</span>
                              ))}
                            </div>
                          </div>
                        )}
                        {(q.follow_up_question && q.follow_up_question.trim()) && (
                          <>
                            <div style={{ color: '#64748b', fontSize: 13, marginTop: 8, marginBottom: 4 }}>Fallback Follow-Up Question:</div>
                            <div style={{ color: '#666', marginBottom: 8, whiteSpace: 'pre-wrap' }}>{q.follow_up_question}</div>
                          </>
                        )}
                      </div>
                    ))
                  ) : (
                    <div style={{ color: '#888' }}>No Apply it on the Job questions.</div>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="modern-flex modern-justify-between modern-items-center modern-mb-4">
        <h2 className="modern-heading" style={{ marginBottom: 0 }}>Course AI</h2>
        <button
          type="button"
          className="modern-button modern-button-secondary"
          onClick={() => navigate(`/course/${id}/ai`)}
        >
          {(course?.reflection_llm_context || '').trim() ? 'Edit Course AI' : 'Add Course AI Context'}
        </button>
      </div>
      <div className="modern-card modern-mb-6">
        {(course?.reflection_llm_context || '').trim() ? (
          <div
            title={course.reflection_llm_context.trim()}
            style={{
              lineHeight: 1.5,
              color: '#334155',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {course.reflection_llm_context.trim().replace(/\s+/g, ' ')}
          </div>
        ) : (
          <div style={{ color: '#94a3b8' }}>No course AI context configured yet.</div>
        )}
      </div>
      
      <LessonModal open={showLessonModal} onClose={() => setShowLessonModal(false)} onSave={handleModalSave} lesson={editLesson} position={editLesson ? editLesson.position : lessons.length + 1} courseId={id} categories={categories} refreshCategories={refreshCategories} isOnboarding={false} section={modalSection} />
      <LessonModal open={showOnboardingModal} onClose={() => setShowOnboardingModal(false)} onSave={handleModalSave} lesson={editLesson} position={null} courseId={id} categories={[]} refreshCategories={undefined} isOnboarding={true} section={null} />

      {showCreateLessonModal && (
        <div className="modern-modal-overlay">
          <div className="modern-modal">
            <button
              type="button"
              onClick={() => { if (!creatingLesson) { setShowCreateLessonModal(false); } }}
              className="modern-modal-close"
            >
              &times;
            </button>
            <h2 className="modern-heading">Add Lesson</h2>

            {!createLessonMode && (
              <>
                <button
                  className="modern-button modern-button-success"
                  style={{ width: '100%', marginBottom: 10 }}
                  onClick={() => { setCreateLessonMode('scratch'); setCreateLessonError(''); }}
                >
                  Start From Scratch
                </button>
                <button
                  className="modern-button modern-button-info"
                  style={{ width: '100%' }}
                  onClick={async () => {
                    setCreateLessonMode('existing');
                    setExistingError('');
                    setExistingCourseId('');
                    setExistingLessons([]);
                    setExistingCourses([]);
                    setExistingLoading(true);
                    try {
                      const { data: courseList } = await supabase
                        .from('courses')
                        .select('id, name, active')
                        .neq('id', parseInt(id))
                        .order('name');
                      setExistingCourses(courseList || []);
                    } catch (e) {
                      setExistingError('Failed to load courses');
                    } finally {
                      setExistingLoading(false);
                    }
                  }}
                >
                  Load Existing Lesson
                </button>
              </>
            )}

            {createLessonMode === 'scratch' && (
              <>
                <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>Lesson Title (optional):</label>
                <input type="text" value={createLessonTitle} onChange={e => setCreateLessonTitle(e.target.value)} placeholder="Enter lesson title" style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ccc', marginBottom: 12 }} />
                {createLessonError && <div style={{ color: 'red', marginBottom: 8 }}>{createLessonError}</div>}
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    className="modern-button modern-button-secondary"
                    disabled={creatingLesson}
                    onClick={() => { setCreateLessonMode(''); setCreateLessonError(''); }}
                    style={{ flex: 1 }}
                  >
                    Back
                  </button>
                  <button
                    onClick={async () => {
                      try {
                        setCreatingLesson(true);
                        setCreateLessonError('');
                        console.log('[COURSE][DB] INSERT lesson', { courseId: parseInt(id), position: lessons.length + 1 });
                        const { data, error } = await supabase.from('lessons')
                          .insert({ course_id: parseInt(id), position: lessons.length + 1, title: (createLessonTitle || '').trim() || null, lesson_type: 'regular' })
                          .select('lesson_id, position, title, lesson_type, description')
                          .single();
                        if (error) throw error;
                        const created = { lesson_id: data.lesson_id, position: data.position, title: data.title, lesson_type: data.lesson_type, description: data.description, questions: [] };
                        setLessons(prev => {
                          const updated = [...prev, created];
                          setCache(SNAPSHOT_KEY, { course, onboarding, lessons: updated, categories });
                          return updated;
                        });
                        setCreatingLesson(false);
                        setShowCreateLessonModal(false);
                        setCreateLessonMode('');
                      } catch (err) {
                        setCreatingLesson(false);
                        setCreateLessonError('Failed to create lesson');
                      }
                    }}
                    disabled={creatingLesson}
                    className="modern-button modern-button-success"
                    style={{ flex: 1 }}
                  >
                    {creatingLesson ? 'Creating…' : 'Create Lesson'}
                  </button>
                </div>
              </>
            )}

            {createLessonMode === 'existing' && (
              <>
                {existingLoading ? <div style={{ color: '#64748b' }}>Loading…</div> : null}
                {existingError ? <div style={{ color: 'red', marginBottom: 8 }}>{existingError}</div> : null}

                <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>Select a course</label>
                <select
                  className="modern-select"
                  value={existingCourseId}
                  onChange={async (e) => {
                    const val = e.target.value;
                    setExistingCourseId(val);
                    setExistingLessons([]);
                    setExistingError('');
                    if (!val) return;
                    setExistingLoading(true);
                    try {
                      const { data: lsns, error: lErr } = await supabase
                        .from('lessons')
                        .select('lesson_id, position, title, lesson_type')
                        .eq('course_id', parseInt(val))
                        .eq('lesson_type', 'regular')
                        .order('position');
                      if (lErr) throw lErr;
                      setExistingLessons(lsns || []);
                    } catch (e2) {
                      setExistingError('Failed to load lessons');
                    } finally {
                      setExistingLoading(false);
                    }
                  }}
                  style={{ width: '100%', marginBottom: 12 }}
                >
                  <option value="">-- choose a course --</option>
                  {existingCourses.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>

                {existingCourseId && (
                  <>
                    <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>Select a lesson to copy</label>
                    <div style={{ maxHeight: 220, overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: 8 }}>
                      {(existingLessons || []).length === 0 ? (
                        <div style={{ padding: 10, color: '#94a3b8' }}>No lessons in this course.</div>
                      ) : (
                        (existingLessons || []).map(lsn => (
                          <button
                            key={lsn.lesson_id}
                            className="modern-button modern-button-secondary"
                            style={{ width: '100%', justifyContent: 'space-between', borderRadius: 0, border: 'none', borderBottom: '1px solid #e2e8f0' }}
                            onClick={async () => {
                              // Implemented below: fetch full lesson content and open the full editor modal with prefill (no DB writes until Save).
                              setExistingError('');
                              setExistingLoading(true);
                              try {
                                // Fetch source lesson title/description
                                const { data: srcLesson, error: srcLErr } = await supabase
                                  .from('lessons')
                                  .select('lesson_id, title, description, lesson_type')
                                  .eq('lesson_id', lsn.lesson_id)
                                  .single();
                                if (srcLErr) throw srcLErr;
                                // Fetch questions
                                const { data: qs, error: qErr } = await supabase
                                  .from('questions')
                                  .select('id, question_type, "order", lesson_id')
                                  .eq('lesson_id', lsn.lesson_id)
                                  .order('order', { ascending: true });
                                if (qErr) throw qErr;
                                const mcIds = (qs || []).filter(q => q.question_type === 'multi_choice').map(q => q.id);
                                const refIds = (qs || []).filter(q => q.question_type === 'reflection').map(q => q.id);
                                const infoIds = (qs || []).filter(q => q.question_type === 'information').map(q => q.id);

                                let mcMap = {}; let mcOptMap = {};
                                if (mcIds.length > 0) {
                                  const { data: mcDetails } = await supabase
                                    .from('questions')
                                    .select('id, multi_choice_questions(question_text), multi_choice_options(option_text,is_correct)')
                                    .in('id', mcIds);
                                  (mcDetails || []).forEach(r => {
                                    const qText = Array.isArray(r.multi_choice_questions)
                                      ? (r.multi_choice_questions[0] && r.multi_choice_questions[0].question_text) || ''
                                      : (r.multi_choice_questions && r.multi_choice_questions.question_text) || '';
                                    mcMap[r.id] = { question_text: qText };
                                    mcOptMap[r.id] = Array.isArray(r.multi_choice_options) ? r.multi_choice_options : [];
                                  });
                                }

                                let refMap = {}; let refCatMap = {};
                                if (refIds.length > 0) {
                                  const { data: refDetails } = await supabase
                                    .from('reflection_questions')
                                    .select('question_id, prompt_text, extra_details, follow_up_question, reflection_question_categories(course_categories(id, category_name))')
                                    .in('question_id', refIds);
                                  (refDetails || []).forEach(r => {
                                    refMap[r.question_id] = { prompt_text: r.prompt_text, extra_details: r.extra_details || '', follow_up_question: r.follow_up_question || '' };
                                    const cats = Array.isArray(r.reflection_question_categories) ? r.reflection_question_categories : [];
                                    refCatMap[r.question_id] = cats
                                      .map(c => c.course_categories?.category_name ? { category_name: c.course_categories.category_name } : null)
                                      .filter(Boolean);
                                  });
                                }

                                let infoMap = {};
                                if (infoIds.length > 0) {
                                  const { data: infoDetails } = await supabase
                                    .from('information_questions')
                                    .select('question_id, title, body_text')
                                    .in('question_id', infoIds);
                                  (infoDetails || []).forEach(r => { infoMap[r.question_id] = { title: r.title, body_text: r.body_text }; });
                                }

                                // Build prefill questions in LessonModal's expected format (no ids; everything will be inserted on Save)
                                const prefillQuestions = (qs || []).map((q) => {
                                  if (q.question_type === 'multi_choice') {
                                    const md = mcMap[q.id] || { question_text: '' };
                                    const opts = mcOptMap[q.id] || [];
                                    return {
                                      type: 'multi_choice',
                                      question_text: md.question_text,
                                      options: opts.map(o => ({ text: o.option_text, is_correct: o.is_correct })),
                                      order: q.order,
                                      isNew: true,
                                    };
                                  }
                                  if (q.question_type === 'reflection') {
                                    const rd = refMap[q.id] || { prompt_text: '', extra_details: '', follow_up_question: '' };
                                    const cats = refCatMap[q.id] || [];
                                    return {
                                      type: 'reflection',
                                      question_text: rd.prompt_text,
                                      extra_details: rd.extra_details || '',
                                      follow_up_question: rd.follow_up_question || '',
                                      categories: cats,
                                      order: q.order,
                                      isNew: true,
                                    };
                                  }
                                  if (q.question_type === 'information') {
                                    const inf = infoMap[q.id] || { title: '', body_text: '' };
                                    return {
                                      type: 'information',
                                      title: inf.title,
                                      body_text: inf.body_text,
                                      order: q.order,
                                      isNew: true,
                                    };
                                  }
                                  return null;
                                }).filter(Boolean);

                                setPrefillDraft({
                                  title: srcLesson?.title || '',
                                  description: srcLesson?.description || '',
                                  questions: prefillQuestions
                                });
                                setShowPrefillLessonModal(true);
                                setShowCreateLessonModal(false);
                                setCreateLessonMode('');
                              } catch (e3) {
                                setExistingError('Failed to load lesson content');
                              } finally {
                                setExistingLoading(false);
                              }
                            }}
                          >
                            <span>{`Lesson ${lsn.position}`}{lsn.title ? `: ${lsn.title}` : ''}</span>
                            <span style={{ color: '#94a3b8' }}>Copy</span>
                          </button>
                        ))
                      )}
                    </div>
                  </>
                )}

                <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                  <button
                    className="modern-button modern-button-secondary"
                    onClick={() => { setCreateLessonMode(''); setExistingError(''); }}
                    style={{ flex: 1 }}
                  >
                    Back
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Full-screen editor for copying an existing lesson (no DB writes until Save) */}
      <LessonModal
        open={showPrefillLessonModal}
        onClose={() => { setShowPrefillLessonModal(false); setPrefillDraft(null); }}
        onSave={handleModalSave}
        lesson={null}
        position={lessons.length + 1}
        courseId={parseInt(id)}
        categories={categories}
        refreshCategories={refreshCategories}
        isOnboarding={false}
        section="all"
        prefill={prefillDraft}
      />

      {lessonToDelete && (
        <div className="modern-modal-overlay">
          <div className="modern-modal">
            <button onClick={cancelDeleteLesson} className="modern-modal-close">&times;</button>
            <h2 className="modern-heading">Are you sure you want to delete this {lessonToDelete.lesson_type === 'onboarding' ? 'onboarding' : 'lesson'}?</h2>
            <p style={{ color: '#64748b', marginBottom: 24 }}>This will permanently delete {lessonToDelete.lesson_type === 'onboarding' ? 'the onboarding' : `Lesson ${lessonToDelete.position}`} and all {lessonToDelete.questions?.length || 0} questions within it. This action cannot be undone.</p>
            {deleteError && <div className="modern-error">{deleteError}</div>}
            <div className="modern-flex modern-gap-4">
              <button className="modern-button modern-button-danger" onClick={confirmDeleteLesson} disabled={deleting}>{deleting ? 'Deleting...' : `Delete ${lessonToDelete.lesson_type === 'onboarding' ? 'Onboarding' : 'Lesson'}`}</button>
              <button className="modern-button modern-button-secondary" onClick={cancelDeleteLesson} disabled={deleting}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {showDeleteCourseModal && (
        <div className="modern-modal-overlay">
          <div className="modern-modal" style={{ borderTop: '6px solid #ef4444' }}>
            <button onClick={() => setShowDeleteCourseModal(false)} className="modern-modal-close">&times;</button>
            <h2 className="modern-heading" style={{ color: '#b91c1c' }}>Delete or Archive Course</h2>
            <p style={{ color: '#64748b', marginBottom: 16 }}>
              If this course has never been used in a bootcamp, it will be permanently deleted along with all onboarding and lessons.
              If it has been used, it will be archived (hidden from selection) instead of deleted. This action cannot be undone.
            </p>
            {deleteCourseError && <div className="modern-error" style={{ marginBottom: 12 }}>{deleteCourseError}</div>}
            <div className="modern-flex modern-gap-4">
              <button className="modern-button modern-button-danger" onClick={confirmDeleteCourse} disabled={deletingCourse}>
                {deletingCourse ? 'Processing…' : 'Proceed'}
              </button>
              <button className="modern-button modern-button-secondary" onClick={() => setShowDeleteCourseModal(false)} disabled={deletingCourse}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Migrate Lesson Modal */}
      {showMigrateModal && lessonToMigrate && (
        <div className="modern-modal-overlay">
          <div className="modern-modal" style={{ borderTop: '6px solid #22c55e' }}>
            <button onClick={() => setShowMigrateModal(false)} className="modern-modal-close">&times;</button>
            <h2 className="modern-heading" style={{ color: '#16a34a' }}>Migrate Lesson</h2>
            <p style={{ color: '#64748b', marginBottom: 12 }}>
              This will remove this lesson from this course and place it at the end of the selected course.
            </p>
            {lessonToMigrate.lesson_type === 'onboarding' ? (
              <div className="modern-error" style={{ marginBottom: 12 }}>Onboarding lessons cannot be migrated.</div>
            ) : (
              <>
                <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>Select target course</label>
                <select
                  className="modern-select"
                  value={migrateTarget}
                  onChange={async e => {
                    const val = e.target.value;
                    setMigrateTarget(val);
                    setMigrateError('');
                    setMigratePositionPreview(null);
                    if (val) {
                      try {
                        const { data: targetLessons } = await supabase
                          .from('lessons')
                          .select('position, lesson_type')
                          .eq('course_id', parseInt(val))
                          .eq('lesson_type', 'regular');
                        const positions = (targetLessons || []).map(l => Number(l.position) || 0);
                        const maxPos = positions.length > 0 ? Math.max(...positions) : 0;
                        const nextPos = maxPos + 1;
                        setMigratePositionPreview(nextPos);
                      } catch {}
                    }
                  }}
                  disabled={migrating}
                  style={{ width: '100%', marginBottom: 12 }}
                >
                  <option value="">-- choose a course --</option>
                  {migrateCourses.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
                {migratePositionPreview != null && (
                  <div style={{ color: '#64748b', marginBottom: 12 }}>Lesson will be positioned at {migratePositionPreview}.</div>
                )}
                {migrateError && <div className="modern-error" style={{ marginBottom: 12 }}>{migrateError}</div>}
                <button
                  className="modern-button modern-button-success"
                  disabled={migrating || !migrateTarget}
                  onClick={async () => {
                    if (!migrateTarget) { setMigrateError('Please select a target course'); return; }
                    try {
                      setMigrating(true);
                      // Compute final position safely on server
                      const { data: targetLessons } = await supabase
                        .from('lessons')
                        .select('position, lesson_type')
                        .eq('course_id', parseInt(migrateTarget))
                        .eq('lesson_type', 'regular');
                      const positions = (targetLessons || []).map(l => Number(l.position) || 0);
                      const maxPos = positions.length > 0 ? Math.max(...positions) : 0;
                      const nextPos = maxPos + 1;
                      // Update lesson
                      const { error: updErr } = await supabase
                        .from('lessons')
                        .update({ course_id: parseInt(migrateTarget), position: nextPos })
                        .eq('lesson_id', lessonToMigrate.lesson_id);
                      setMigrating(false);
                      if (updErr) { setMigrateError('Failed to migrate lesson'); return; }
                      // Optimistically update local state: remove from current and reindex
                      setLessons(prev => {
                        const filtered = prev.filter(l => l.lesson_id !== lessonToMigrate.lesson_id);
                        const reindexed = filtered.map((l, idx) => ({ ...l, position: idx + 1 }));
                        setCache(SNAPSHOT_KEY, { course, onboarding, lessons: reindexed, categories });
                        return reindexed;
                      });
                      setShowMigrateModal(false);
                    } catch (e) {
                      setMigrating(false);
                      setMigrateError('Unexpected error migrating lesson');
                    }
                  }}
                  style={{ width: '100%' }}
                >
                  {migrating ? 'Migrating…' : 'Migrate Lesson'}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default CourseDetail; 